/**
 * Offline write queue (iter 45).
 * ---------------------------------------------------------------
 * Stores mutations (POST/PATCH/PUT/DELETE) that failed because the
 * device is offline, so we can replay them once connectivity comes
 * back. Uses IndexedDB directly (no deps) with an auto-incrementing
 * key so we preserve insertion order (FIFO).
 *
 * Events emitted on `window`:
 *   - `powereffect:queue-changed` (detail: { size })  after every add/remove
 *   - `powereffect:queue-drained` (detail: { ok, failed }) after drain finishes
 *
 * Design choices:
 *   - We DO NOT fake a successful response for the calling code. The write
 *     is enqueued and the caller sees an OfflineQueuedError. It's up to the
 *     UI to toast "queued offline" and re-fetch on next online cycle.
 *   - We only queue same-origin /api/* mutations. Auth flows (/api/auth/*)
 *     are explicitly excluded so a logout doesn't queue up weirdly.
 * ---------------------------------------------------------------
 */

const DB_NAME = "powereffect-offline";
const DB_VERSION = 1;
const STORE = "queue";

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function tx(mode) {
  const db = await openDb();
  return db.transaction(STORE, mode).objectStore(STORE);
}

function emit(name, detail) {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* ignore */ }
}

/** Add a mutation to the queue. Returns the assigned id. */
export async function enqueue({ method, url, data, entityId, label, userId }) {
  const store = await tx("readwrite");
  const record = {
    method,
    url,
    data: data ?? null,
    entityId: entityId || null,
    // SEC-001: bind each queued item to the user that authored it so we
    // don't replay another user's writes if the browser is shared.
    userId: userId || null,
    label: label || `${method} ${url}`,
    createdAt: new Date().toISOString(),
  };
  const id = await new Promise((resolve, reject) => {
    const req = store.add(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  const sz = await size();
  emit("powereffect:queue-changed", { size: sz });
  return id;
}

/** List all queued mutations (FIFO order). */
export async function list() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

/** Number of items in the queue. */
export async function size() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = () => reject(req.error);
  });
}

/** Remove a single item by id. */
export async function remove(id) {
  const store = await tx("readwrite");
  await new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  const sz = await size();
  emit("powereffect:queue-changed", { size: sz });
}

/** Wipe the entire queue (used by "Discard queued changes" action). */
export async function clearAll() {
  const store = await tx("readwrite");
  await new Promise((resolve, reject) => {
    const req = store.clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  emit("powereffect:queue-changed", { size: 0 });
}

/**
 * Replay every queued mutation against the live axios client. Runs in FIFO
 * order and STOPS on the first network failure (we're still offline). Any
 * mutation that returns a 4xx is treated as "server rejected the change" —
 * we drop it from the queue and record it in the returned `failed` array so
 * the UI can surface it.
 *
 * SEC-001: `currentUserId` (if provided) is compared against each item's
 * stored `userId`. Mismatches are DROPPED — never replayed under a different
 * session, and never left in the queue where they'd be exposed to whoever
 * logs in next. Legacy items (no userId stamp) are still replayed under the
 * current session for backwards compatibility with pre-fix queue entries.
 */
export async function drain(apiClient, currentUserId = null) {
  const items = await list();
  const ok = [];
  const failed = [];
  const dropped = [];
  for (const item of items) {
    if (currentUserId && item.userId && item.userId !== currentUserId) {
      // Not our queued item — drop it silently rather than replay under
      // whoever's session is currently active.
      await remove(item.id);
      dropped.push(item);
      continue;
    }
    try {
      const cfg = { headers: {} };
      if (item.entityId) cfg.headers["X-Entity-Id"] = item.entityId;
      cfg.headers["X-Offline-Replay"] = "1"; // observable server-side + skips re-queue
      const method = (item.method || "post").toLowerCase();
      if (method === "post" || method === "patch" || method === "put") {
        await apiClient[method](item.url, item.data || {}, cfg);
      } else if (method === "delete") {
        await apiClient.delete(item.url, cfg);
      } else {
        throw new Error(`Unsupported method '${item.method}'`);
      }
      await remove(item.id);
      ok.push(item);
    } catch (err) {
      // Network-level failure → stop; we're still offline.
      if (!err?.response) {
        emit("powereffect:queue-drained", { ok, failed, dropped, halted: true });
        return { ok, failed, dropped, halted: true };
      }
      // Server-side failure → drop from queue, remember for UI.
      await remove(item.id);
      failed.push({ item, status: err.response.status, detail: err.response?.data?.detail || err.message });
    }
  }
  emit("powereffect:queue-drained", { ok, failed, dropped, halted: false });
  return { ok, failed, dropped, halted: false };
}

/** True when the browser reports we're online. */
export const isOnline = () => (typeof navigator !== "undefined" ? navigator.onLine !== false : true);

/** Custom error class thrown by the axios wrapper when a mutation was queued. */
export class OfflineQueuedError extends Error {
  constructor({ label, queueId } = {}) {
    super("Change queued for sync when you're back online");
    this.name = "OfflineQueuedError";
    this.offlineQueued = true;
    this.queueId = queueId;
    this.label = label;
    // Give this a synthetic response shape so existing catch blocks like
    //   toast.error(err.response?.data?.detail || "Failed")
    // surface a friendly message instead of a generic "Failed".
    this.response = {
      status: 202,
      data: { offline_queued: true, detail: "Saved offline — will sync when you're back online" },
    };
  }
}
